import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { DigitalVaultBinView } from '../../views';
import { CacheStorageService, DigitalVaultService, LocalStorageService, DigitalVaultDocumentService } from '../../../services'
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";

const DigitalVaultBinAction = (props) => {
    const {
        toggleCompletedRequesting,
        toggleLoadingRequesting,
        toggleErrorRequesting,
        t,
        history,
        isDeletingDocument = false
    } = props;

    const [isLoading, setIsLoading] = useState(true);
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('0');
    const [documents, setDocuments] = useState([]);
    const [selectedDocument, setSelectedDocument] = useState(null);

    const [viewDeleteDialog, setViewDeleteDialog] = useState(false);

    const url = process.env.REACT_APP_MIFIRMA_API;
    const localStorageService = new LocalStorageService();
    const token = localStorageService.getLocalStorageItem("token").idToken;
    const timezone = localStorageService.getLocalStorageItem("timezone");
    const digitalVaultService = useMemo(() => new DigitalVaultService(url), [url])
    const digitalVaultDocumentService = useMemo(() => new DigitalVaultDocumentService(url), [url])
    const cacheService = useMemo(() => new CacheStorageService(), [])

    const loadDocuments = useCallback(async (deleteCache = false) => {
        setIsLoading(true);

        try {
            if (deleteCache) await cacheService.deleteUrlFromCache("/digital-vault-trash")
            const response = await digitalVaultService.getTrashDocuments(token, timezone);

            if (response.statusCode === 200 && response.data) {
                setDocuments(response.data.documents || [])
                let companyOptions = response.data.companies || []
                companyOptions.sort((a, b) => a.companyName.toLowerCase().localeCompare(b.companyName.toLowerCase()))
                companyOptions = [{ id: '0', companyName: t("digitalVaultBinView.all.companies") }, ...companyOptions]
                setCompanies(companyOptions);
            } else {
                throw new Error(response.data.message || t("digitalVaultBinView.errors.general"))
            }
            setIsLoading(false);
        } catch (e) {
            toggleErrorRequesting(true, e.message, 4000, true)
        }
    }, [cacheService, digitalVaultService, timezone, token, t, toggleErrorRequesting])

    const startDeleteFlow = async (deleteDoc, ids) => {
        const targetIds = ids ?? (selectedDocument ? [selectedDocument.id] : [])
        if (!targetIds.length) return

        setViewDeleteDialog(false);
        toggleLoadingRequesting(true);

        try {
            const results = targetIds.length > 1
                ? await Promise.all(
                    targetIds.map(id => digitalVaultService.startDeleteWorkflow(id, deleteDoc, token))
                )
                : [await digitalVaultService.startDeleteWorkflow(targetIds[0], deleteDoc, token)]

            const anyError = results.find(r => r?.statusCode && r.statusCode !== 200)
            if (anyError) throw new Error(anyError.message || t("digitalVaultBinView.errors.general"))

            toggleLoadingRequesting(false)
            toggleCompletedRequesting(true)

            setTimeout(() => {
                toggleCompletedRequesting(false)
                history.push("/")
            }, 4000)
        } catch (e) {
            toggleErrorRequesting(true, e.message, 4000, false)
        } finally {
            toggleLoadingRequesting(false)
        }
    }

    const deleteDocument = () => startDeleteFlow(true)
    const restoreDocument = async () => {
        if (!selectedDocument?.id) return
        if (selectedDocument?.isDeletingDocument || isDeletingDocument) {
            return startDeleteFlow(false)
        }
        toggleLoadingRequesting(true)
        try {
            const response = await digitalVaultDocumentService.updateDocumentStatus(
                selectedDocument.id,
                'Restaurar',
                token
            )

            if (response?.statusCode === 200) {
                toggleLoadingRequesting(false)
                toggleCompletedRequesting(true)
                setTimeout(() => {
                    toggleCompletedRequesting(false)
                    history.push('/')
                }, 4000)
            } else {
                throw new Error(response?.message || t('digitalVaultBinView.errors.general'))
            }
        } catch (e) {
            toggleErrorRequesting(true, e.message, 4000, false)
        } finally {
            toggleLoadingRequesting(false)
        }
    }
    const deleteMany = (ids) => startDeleteFlow(true, ids)

    useEffect(() => {
        loadDocuments();
        return () => {
            cacheService.deleteUrlFromCache("/digital-vault-trash")
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <DigitalVaultBinView
            isLoading={isLoading}
            companies={companies}
            selectedCompanyId={selectedCompanyId}
            setSelectedCompanyId={setSelectedCompanyId}
            documents={documents}
            selectedDocument={selectedDocument}
            setSelectedDocument={setSelectedDocument}
            deleteMany={deleteMany}
            deleteDocument={deleteDocument}
            restoreDocument={restoreDocument}
            viewDeleteDialog={viewDeleteDialog}
            setViewDeleteDialog={setViewDeleteDialog}
            isDeletingDocument={isDeletingDocument}
        />
    )
}

export default withRouter(withTranslation()(DigitalVaultBinAction));